import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  InputAdornment,
  Tooltip,
} from "@mui/material"
import {
  ExpandMore,
  Assignment,
  PlayCircleOutline,
  History,
  AttachFile,
  Lock,
  Person,
  CheckCircle,
  Warning,
  Build,
} from "@mui/icons-material"
import dayjs, { formatDateForDisplay, parseStringToDayjs } from "../utils/dayjsConfig"
import { showSuccess, showError } from "../utils/alertUtils"
import { useQuery } from "@tanstack/react-query"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"

// ⭐ IMPORTACIÓN DE SU COMPONENTE GLOBAL DE DOCUMENTOS
import DocumentosAsociadosModal from "../../../components/Global/DocumentosAsociadosModal/index"
import DocumentosAsociadosTabla from "../../../components/Global/DocumentosAsociadosModal/DocumentosAsociadosTabla"

const getColorByStatus = (status) => {
  switch (status) {
    case "PENDIENTE":
      return "warning"
    case "EN_PROCESO":
      return "primary"
    case "COMPLETADA":
      return "success"
    case "CANCELADA":
      return "error"
    case "REPROGRAMADA":
      return "default"
    default:
      return "default"
  }
}

// ============================================
// COMPONENTES INTERNOS PARA LOS TIPOS DE TAREA
// ============================================

const TipoTareaTexto = ({ value, onChange, disabled, obligatoria }) => (
  <TextField
    fullWidth
    label="Respuesta / Detalle Técnico"
    multiline
    rows={4}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    margin="normal"
    size="small"
    disabled={disabled}
    placeholder="Describa los hallazgos o resultados..."
    required={obligatoria}
    inputProps={{ maxLength: 5000 }}
    sx={{ bgcolor: disabled ? "#f5f5f5" : "#fff" }}
  />
)

const TipoTareaLista = ({ opciones, value, onChange, disabled, obligatoria }) => (
  <FormControl fullWidth size="small" margin="normal" required={obligatoria}>
    <InputLabel>Seleccione un resultado</InputLabel>
    <Select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      label="Seleccione un resultado"
      disabled={disabled}
      sx={{ bgcolor: disabled ? "#f5f5f5" : "#fff" }}
    >
      {opciones.map((opcion) => (
        <MenuItem key={opcion.pregsecuen} value={opcion.pregsecuen}>
          {opcion.pregdescri} {opcion.pregRespuesta && " ✅"}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

const TipoTareaMultiple = ({ opciones, value, onChange, disabled, obligatoria }) => {
  const handleChange = (secuencia) => {
    const newValue = Array.isArray(value) ? [...value] : []
    const index = newValue.indexOf(secuencia)
    if (index === -1) newValue.push(secuencia)
    else newValue.splice(index, 1)
    onChange(newValue.sort((a, b) => a - b))
  }

  return (
    <FormControl fullWidth size="small" margin="normal" required={obligatoria}>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: "bold" }}>
        Marque las opciones aplicables:
      </Typography>
      <Paper variant="outlined" sx={{ maxHeight: 200, overflow: "auto", p: 1, bgcolor: disabled ? "#f5f5f5" : "#fff" }}>
        {opciones.map((opcion) => (
          <FormControlLabel
            key={opcion.pregsecuen}
            control={
              <Checkbox
                checked={Array.isArray(value) && value.includes(opcion.pregsecuen)}
                onChange={() => handleChange(opcion.pregsecuen)}
                disabled={disabled}
                size="small"
              />
            }
            label={opcion.pregdescri}
            sx={{ mb: 0.5, display: "flex" }}
          />
        ))}
      </Paper>
    </FormControl>
  )
}

// ============================================
// QUERY PARA OBTENER DATOS DEL EVENTO
// ============================================

const useGetEventoData = (eventocodigo, setEsTareaConProcesoTecnicentro) => {
  return useQuery({
    queryKey: ["EjecucionTareas", "getEventoData", eventocodigo],
    queryFn: async () => {
      if (!eventocodigo) return
      const options = {
        method: "POST",
        body: JSON.stringify({ eventocodigo }),
        headers: { "Content-Type": "application/json", "Access-Control-Allow-Origin": "*" },
      }
      let response = await fetchwrapper("/Agenda/getSpecificEvent", options)
      response = await response.json()
      return response.data
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    onError: (error) => console.log("Error fetching evento data:", error.message),
  })
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const ModalEjecucionTarea = ({ open, onClose, eventocodigo }) => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    status: "",
    porcentaje: 0,
    comentario: "",
    respuestaTextoLibre: "",
    respuestaListaSecuencia: null,
    respuestaMultipleSecuencias: [],
    fechaEjecucionReal: null,
  })

  const [ejecucionFueraRango, setEjecucionFueraRango] = useState({ fuera: false, tipo: "", mensaje: "" })
  const [esTareaConProcesoTecnicentro, setEsTareaConProcesoTecnicentro] = useState(false)

  // ⭐ ESTADOS DEL MODAL GLOBAL
  const [isModalDocsOpen, setIsModalDocsOpen] = useState(false)

  // Control de Acordeones
  const [expandedPanels, setExpandedPanels] = useState({
    panelInfo: true,
    panelEjecucion: true,
    panelDocumentos: false,
    panelHistorial: false,
  })

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanels((prev) => ({ ...prev, [panel]: isExpanded }))
  }

  const {
    data: eventoDataResponse = {},
    isLoading: isLoadingEventoData,
    isError: isErrorEventoData,
    isFetching: isFetchingEventoData,
  } = useGetEventoData(eventocodigo, setEsTareaConProcesoTecnicentro)

  const eventoData = eventoDataResponse?.data || eventoDataResponse || {}
  const evento = eventoData.evento || {}
  const tarea = eventoData.tarea || {}
  const opcionesTarea = eventoData.opcionesTarea || []
  const historialEjecuciones = eventoData.historialEjecuciones || []
  const respuestaTextoLibre = eventoData.respuestaTextoLibre || ""
  const respuestaListaSecuencia = eventoData.respuestaListaSecuencia || null
  const respuestaMultipleSecuencias = eventoData.respuestaMultipleSecuencias || []

  const accordionStyle = {
    mb: 2,
    borderRadius: "8px !important",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    "&:before": { display: "none" },
  }
  const summaryStyle = {
    bgcolor: "#f4f6f8",
    borderRadius: "8px",
    "&.Mui-expanded": { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, bgcolor: "#ebf1f5" },
  }
  const esTecnicoFijo = evento.eventotecnicoreq === 1 || evento.eventotecnicoreq === true

  useEffect(() => {
    if (eventoData?.evento?.procesocod === "TECNICENTRO") {
      setEsTareaConProcesoTecnicentro(true)
    } else {
      setEsTareaConProcesoTecnicentro(false)
    }
  }, [eventoData])

  const estadosPermitidos = {
    PENDIENTE: ["EN_PROCESO", "COMPLETADA", "CANCELADA", "REPROGRAMADA"],
    EN_PROCESO: ["EN_PROCESO", "COMPLETADA", "CANCELADA", "REPROGRAMADA"],
    COMPLETADA: ["EN_PROCESO"],
    CANCELADA: [],
    REPROGRAMADA: [],
  }

  useEffect(() => {
    if (open && evento && Object.keys(evento).length > 0) {
      setFormData({
        status: evento.eventostatus || "PENDIENTE",
        porcentaje: evento.porcentajeavance || 0,
        comentario: "",
        respuestaTextoLibre: respuestaTextoLibre || "",
        respuestaListaSecuencia: respuestaListaSecuencia || null,
        respuestaMultipleSecuencias: respuestaMultipleSecuencias || [],
      })
      verificarHorarioEjecucion()
    }
  }, [open, evento, respuestaTextoLibre, respuestaListaSecuencia, respuestaMultipleSecuencias])

  const verificarHorarioEjecucion = () => {
    if (!evento || !evento.eventofecha || !evento.eventohorainicio || !evento.eventohorafin) return

    const ahora = dayjs()
    const fechaEvento = parseStringToDayjs(evento.eventofecha)
    const inicioProgramado = parseStringToDayjs(evento.eventohorainicio)
    const finProgramado = parseStringToDayjs(evento.eventohorafin)

    const inicioCompleto = fechaEvento
      .set("hour", inicioProgramado.hour())
      .set("minute", inicioProgramado.minute())
      .set("second", 0)
      .set("millisecond", 0)
    const finCompleto = fechaEvento
      .set("hour", finProgramado.hour())
      .set("minute", finProgramado.minute())
      .set("second", 0)
      .set("millisecond", 0)

    let fuera = false
    let tipo = ""
    let mensaje = ""

    if (ahora.isBefore(inicioCompleto)) {
      fuera = true
      tipo = "ANTICIPADA"
      mensaje = `Se está ejecutando antes del horario programado (${formatDateForDisplay(inicioCompleto)})`
    } else if (ahora.isAfter(finCompleto)) {
      fuera = true
      tipo = "ATRASADA"
      mensaje = `Se está ejecutando después del horario límite programado (${formatDateForDisplay(finCompleto)})`
    }
    setEjecucionFueraRango({ fuera, tipo, mensaje })
  }

  const handleChange = (field) => (event) => {
    const value = event.target.value
    if (field === "status" && value === "COMPLETADA") {
      setFormData((prev) => ({ ...prev, [field]: value, porcentaje: 100, fechaEjecucionReal: null }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }))
    }
  }

  const handleRespuestaChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validarGuardado = () => {
    if (!formData.comentario.trim() || formData.comentario.trim().length < 10) {
      showError("Debe escribir un comentario de al menos 10 caracteres")
      return false
    }
    const estadoActual = evento.eventostatus || "PENDIENTE"
    const estadosValidos = estadosPermitidos[estadoActual] || []
    if (formData.status !== estadoActual && !estadosValidos.includes(formData.status)) {
      showError(`No puede cambiar de ${estadoActual} a ${formData.status}`)
      return false
    }
    if (["CANCELADA", "REPROGRAMADA"].includes(formData.status)) {
      if (
        !window.confirm(
          `⚠️ ATENCIÓN: Al marcar como ${formData.status}, la tarea quedará BLOQUEADA permanentemente. ¿Continuar?`,
        )
      )
        return false
    }
    if (tarea.pregobligatoria) {
      if (tarea.pregtipo === "U" && !formData.respuestaTextoLibre?.trim()) {
        showError("Debe completar el campo de texto (tarea obligatoria)")
        return false
      }
      if (tarea.pregtipo === "L" && !formData.respuestaListaSecuencia) {
        showError("Debe seleccionar una opción (tarea obligatoria)")
        return false
      }
      if (
        tarea.pregtipo === "M" &&
        (!Array.isArray(formData.respuestaMultipleSecuencias) || formData.respuestaMultipleSecuencias.length === 0)
      ) {
        showError("Debe seleccionar al menos una opción")
        return false
      }
    }
    if (formData.status === "COMPLETADA" && formData.fechaEjecucionReal && !formData.fechaEjecucionReal.isValid()) {
      showError("La fecha seleccionada no es válida")
      return false
    }
    return true
  }

  // ⭐ FUNCIÓN ACTUALIZADA: Conexión directa a su endpoint real
  const handleGuardar = async () => {
    if (!validarGuardado()) return
    setLoading(true)

    try {
      const datosGuardar = {
        eventocodigo,
        eventostatus: formData.status,
        porcentajeavance: formData.porcentaje,
        comentario: formData.comentario,
        ejecucionFueraRango: ejecucionFueraRango.fuera,
        tipoFueraRango: ejecucionFueraRango.fuera ? ejecucionFueraRango.tipo : null,
        respuestaTextoLibre: tarea.pregtipo === "U" ? formData.respuestaTextoLibre : null,
        respuestaListaSecuencia: tarea.pregtipo === "L" ? formData.respuestaListaSecuencia : null,
        respuestaMultipleSecuencias:
          tarea.pregtipo === "M"
            ? Array.isArray(formData.respuestaMultipleSecuencias)
              ? formData.respuestaMultipleSecuencias
              : []
            : null,
        ...(formData.status === "COMPLETADA" && {
          fechaEjecucionReal: formData.fechaEjecucionReal
            ? formData.fechaEjecucionReal.format("YYYY-MM-DD HH:mm:ss")
            : null,
        }),
        procesocod: eventoData?.evento?.procesocod,
        ...(esTareaConProcesoTecnicentro
          ? {
              placa: evento.placa,
              clicodigo: evento.clicodigo,
              clinombre: evento.clinombre,
            }
          : {}),
      }

      const options = {
        method: "POST",
        body: JSON.stringify(datosGuardar),
        headers: { "Content-Type": "application/json", "Access-Control-Allow-Origin": "*" },
      }

      // ✅ APUNTA AL ENDPOINT CORRECTO DEL ARCHIVO saveEjecucionEvento.py
      await fetchwrapper("/Agenda/saveEjecucionEvento", options)

      showSuccess("Ejecución registrada con éxito en la bitácora")
      onClose() // Esto cierra el modal y dispara refetchEventos en index.js
    } catch (error) {
      console.error(error)
      showError(error.message || "Error al comunicarse con el servidor")
    } finally {
      setLoading(false)
    }
  }

  const esEstadoTerminal = ["CANCELADA", "REPROGRAMADA"].includes(evento.eventostatus)
  const esEditable = !esEstadoTerminal
  const isLoading = isLoadingEventoData || isFetchingEventoData

  if (!open) return null

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={40} sx={{ mr: 2, color: "#196C87" }} />
          <Typography variant="h6" color="textSecondary">
            Desplegando torre de ejecución...
          </Typography>
        </DialogContent>
      </Dialog>
    )
  }

  if (isErrorEventoData || !evento || Object.keys(evento).length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">No se encontraron datos válidos para este evento.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const pregtipo = tarea.pregtipo
  const pregobligatoria = tarea.pregobligatoria
  const opcionesActivas = opcionesTarea.filter((op) => op.pregstatus === "A")
  const textoRecurrencia =
    evento.eventorecurennum > 0
      ? `${evento.eventorecuren} (${evento.eventorecurensecuen}/${evento.eventorecurennum})`
      : null

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick") onClose()
      }}
      maxWidth="md"
      fullWidth
      sx={{ "& .MuiDialog-paper": { borderRadius: 3, bgcolor: "#fbfcfd", minHeight: "85vh" } }}
    >
      <DialogTitle sx={{ bgcolor: "#196C87", color: "white", py: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Build /> {evento.eventocodigo}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {evento.pregdescri}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Chip
              label={evento.eventostatus}
              color={getColorByStatus(evento.eventostatus)}
              sx={{ fontWeight: "bold", bgcolor: "white", color: "#196C87" }}
            />
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
              Asignado a: {evento.usrnombre}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {ejecucionFueraRango.fuera && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} icon={<Warning />}>
            <Typography variant="body2">
              <strong>FUERA DE RANGO:</strong> {ejecucionFueraRango.mensaje}
            </Typography>
          </Alert>
        )}
        {esEstadoTerminal && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>TAREA {evento.eventostatus}</strong> - Esta tarea ha sido cerrada y bloqueada para ediciones.
            </Typography>
          </Alert>
        )}

        <Accordion
          expanded={expandedPanels.panelInfo}
          onChange={handleAccordionChange("panelInfo")}
          sx={accordionStyle}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={summaryStyle}>
            <Typography fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1, color: "#196C87" }}>
              <Assignment fontSize="small" /> 1. Contexto de la Tarea
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3, bgcolor: "white" }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                  Cliente Asociado (Solo Lectura)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={
                    evento.clinombre
                      ? `${evento.clinombre.replace(/\s*\(\d+\)$/, "")} (${evento.clicodigo})`
                      : "Sin cliente asignado"
                  }
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mt: 0.5, bgcolor: "#f9f9f9" }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                  Restricción de Personal
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {esTecnicoFijo ? (
                    <Chip
                      icon={<Lock />}
                      label="Técnico Fijo Requerido (Intransferible)"
                      color="error"
                      variant="outlined"
                    />
                  ) : (
                    <Chip icon={<Person />} label="Técnico Móvil (Reasignable)" color="success" variant="outlined" />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                  Horario Programado
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  📅 {evento.eventofecha ? parseStringToDayjs(evento.eventofecha).format("DD/MM/YYYY") : "N/A"} <br />
                  🕒 {evento.eventohorainicio
                    ? parseStringToDayjs(evento.eventohorainicio).format("HH:mm")
                    : "N/A"} - {evento.eventohorafin ? parseStringToDayjs(evento.eventohorafin).format("HH:mm") : "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                  Detalles Adicionales
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Institución:</strong> {tarea.instidescri || "N/A"} <br />
                  <strong>Presencial:</strong> {tarea.esPresencial ? "Sí" : "No"} <br />
                  {textoRecurrencia && (
                    <span>
                      <strong>Recurrencia:</strong> {textoRecurrencia}
                    </span>
                  )}
                </Typography>
              </Grid>

              {esTareaConProcesoTecnicentro && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                    Datos de Vehículo (Solo Lectura)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={evento.placa || "Sin placa registrada"}
                    InputProps={{ readOnly: true }}
                    sx={{ mt: 0.5, bgcolor: "#f9f9f9", width: "50%" }}
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanels.panelEjecucion}
          onChange={handleAccordionChange("panelEjecucion")}
          sx={accordionStyle}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={summaryStyle}>
            <Typography fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1, color: "#196C87" }}>
              <PlayCircleOutline fontSize="small" /> 2. Formulario de Ejecución
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3, bgcolor: "white" }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Estado Actual</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleChange("status")}
                    label="Estado Actual"
                    disabled={!esEditable || loading}
                  >
                    {(estadosPermitidos[evento.eventostatus || "PENDIENTE"] || []).map((estado) => (
                      <MenuItem key={estado} value={estado}>
                        {estado}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Porcentaje de avance (%)"
                  type="number"
                  value={formData.porcentaje}
                  onChange={handleChange("porcentaje")}
                  size="small"
                  disabled={!esEditable || loading || formData.status === "COMPLETADA"}
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                />
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {[0, 25, 50, 75, 100].map((percent) => (
                    <Button
                      key={percent}
                      variant="outlined"
                      size="small"
                      onClick={() => handleChange("porcentaje")({ target: { value: percent } })}
                      disabled={!esEditable || formData.status === "COMPLETADA"}
                    >
                      {percent}%
                    </Button>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  {pregtipo === "U"
                    ? "📝 Texto Libre"
                    : pregtipo === "L"
                      ? "📋 Lista de Opciones"
                      : "☑️ Opciones Múltiples"}
                  {pregobligatoria && " (Obligatorio)"}
                </Typography>

                {pregtipo === "U" && (
                  <TipoTareaTexto
                    value={formData.respuestaTextoLibre}
                    onChange={(val) => handleRespuestaChange("respuestaTextoLibre", val)}
                    disabled={!esEditable || loading}
                    obligatoria={pregobligatoria}
                  />
                )}
                {pregtipo === "L" && (
                  <TipoTareaLista
                    opciones={opcionesActivas}
                    value={formData.respuestaListaSecuencia}
                    onChange={(val) => handleRespuestaChange("respuestaListaSecuencia", val)}
                    disabled={!esEditable || loading}
                    obligatoria={pregobligatoria}
                  />
                )}
                {pregtipo === "M" && (
                  <TipoTareaMultiple
                    opciones={opcionesActivas}
                    value={formData.respuestaMultipleSecuencias}
                    onChange={(val) => handleRespuestaChange("respuestaMultipleSecuencias", val)}
                    disabled={!esEditable || loading}
                    obligatoria={pregobligatoria}
                  />
                )}
              </Grid>

              {formData.status === "COMPLETADA" && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 1, p: 0.5 }}>
                    Al marcar como completada, el avance se fija al 100%.
                  </Alert>
                  <TextField
                    fullWidth
                    label="Fecha y hora real de cierre (Opcional)"
                    type="datetime-local"
                    value={formData.fechaEjecucionReal ? formData.fechaEjecucionReal.format("YYYY-MM-DDTHH:mm") : ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        fechaEjecucionReal: e.target.value ? dayjs(e.target.value) : null,
                      }))
                    }
                    size="small"
                    disabled={!esEditable || loading}
                    InputLabelProps={{ shrink: true }}
                    helperText="Si deja este campo vacío, se usará el reloj del servidor al guardar."
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comentario / Novedades de la ejecución *"
                  multiline
                  rows={3}
                  value={formData.comentario}
                  onChange={handleChange("comentario")}
                  size="small"
                  disabled={!esEditable || loading}
                  required
                  helperText={`Requerido para bitácora - Mínimo 10 caracteres (${formData.comentario.length}/1000)`}
                  error={formData.comentario.length > 0 && formData.comentario.length < 10}
                  inputProps={{ maxLength: 1000 }}
                  sx={{ bgcolor: !esEditable ? "#f5f5f5" : "#fff" }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ⭐ ACORDEÓN 3: EVIDENCIAS INTEGRADAS CON EL MODAL GLOBAL */}
        <Accordion
          expanded={expandedPanels.panelDocumentos}
          onChange={handleAccordionChange("panelDocumentos")}
          sx={accordionStyle}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={summaryStyle}>
            <Typography fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1, color: "#196C87" }}>
              <AttachFile fontSize="small" /> 3. Evidencias y Anexos
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3, bgcolor: "white" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="textSecondary">
                Administre fotografías, P12, credenciales o documentos de respaldo para esta tarea.
              </Typography>
              {!esEstadoTerminal && (
                <Button
                  variant="contained"
                  startIcon={<AttachFile />}
                  onClick={() => setIsModalDocsOpen(true)}
                  size="small"
                  sx={{ bgcolor: "#196C87" }}
                >
                  Agregar Evidencia
                </Button>
              )}
            </Box>

            {/* TABLA GLOBAL PARA VISUALIZAR LOS DOCUMENTOS */}
            <DocumentosAsociadosTabla qgenero={eventocodigo} procqgenero="gdocmeventos" />

            {/* MODAL GLOBAL PARA INSERTAR/IMPORTAR DOCUMENTOS */}
            <DocumentosAsociadosModal
              isOpen={isModalDocsOpen}
              onClose={() => setIsModalDocsOpen(false)}
              contexto={{
                docqgenero: eventocodigo, // Dueño: el código del evento (EVA...)
                docprocqgenero: "gdocmeventos", // Origen: la tabla de eventos
                docsecuen: 1,
              }}
              onSuccess={() => {
                // Al cerrar con éxito, el queryClient de React Query recargará la tabla automáticamente
                console.log("Evidencias registradas correctamente en el evento", eventocodigo)
              }}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanels.panelHistorial}
          onChange={handleAccordionChange("panelHistorial")}
          sx={accordionStyle}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={summaryStyle}>
            <Typography fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1, color: "#196C87" }}>
              <History fontSize="small" /> 4. Bitácora de Movimientos
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, bgcolor: "white" }}>
            {historialEjecuciones.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="textSecondary" fontStyle="italic">
                  Aún no existen registros previos para esta tarea.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: "auto", p: 2, bgcolor: "#fafafa" }}>
                {historialEjecuciones
                  .slice()
                  .reverse()
                  .map((item, index) => {
                    // ⭐ NORMALIZACIÓN: Buscamos los campos en minúsculas para asegurar compatibilidad
                    const fecha = item.fechaejecucionreal || item.FechaEjecucionReal
                    const usuario = item.tranusuisys || item.usuario
                    const comentario = item.comentario
                    const statusAnt = item.statusanterior || item.statusAnterior
                    const statusNue = item.statusnuevo || item.statusNuevo
                    const porcAnt = item.porcentajeanterior || item.porcentajeAnterior
                    const porcActual = item.porcentajeavance

                    return (
                      <Paper key={index} elevation={0} sx={{ mb: 2, p: 2, border: "1px solid #eee" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="caption" fontWeight="bold" color="primary">
                            {fecha ? dayjs(fecha).format("DD/MM/YYYY HH:mm") : "N/A"}
                          </Typography>
                          <Chip
                            label={`Por: ${usuario}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ my: 1, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                          "{comentario}"
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Typography variant="caption" color="textSecondary">
                            <strong>Estado:</strong>{" "}
                            {statusAnt === statusNue ? statusNue : `${statusAnt} ➔ ${statusNue}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            <strong>Avance:</strong>{" "}
                            {porcAnt === porcActual ? `${porcActual}%` : `${porcAnt}% ➔ ${porcActual}%`}
                          </Typography>
                        </Box>
                      </Paper>
                    )
                  })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 3, bgcolor: "#f8f9fa", borderTop: "1px solid #eee" }}>
        <Button onClick={onClose} disabled={loading} color="inherit" variant="outlined">
          {esEstadoTerminal ? "Cerrar Visualizador" : "Cancelar"}
        </Button>
        {esEditable && (
          <Button
            onClick={handleGuardar}
            variant="contained"
            disabled={loading || formData.comentario.length < 10}
            sx={{ px: 4, py: 1, bgcolor: "#196C87", "&:hover": { bgcolor: "#125166" } }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Guardar Ejecución"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ModalEjecucionTarea
